import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

interface AuthContextType {
  isAuthenticated: boolean;
  userInfo: { username: string; email: string };
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  logout: () => void;
  validateToken: (token: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return "http://dynasty-app.io/api"; // Production URL
  }
  return "http://localhost:8080/api"; // Development URL
};

const API_BASE_URL = process.env.REACT_APP_API_URL || getApiBaseUrl();

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState({ username: "", email: "" });

  const login = async (usernameOrEmail: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/login/`, {
        username_or_email: usernameOrEmail,
        password: password,
      });

      const token = response.data.token;
      localStorage.setItem("token", token);
      axios.defaults.headers.common["Authorization"] = `Token ${token}`;
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Login error details:", error);
      if (axios.isAxiosError(error)) {
        console.error("Server response status:", error.response?.status);
        console.error("Server response headers:", error.response?.headers);
        console.error("Server response data:", error.response?.data);
        console.error("Request config:", error.config);
      }
      throw new Error("Invalid username/email or password");
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    setUserInfo({ username: "", email: "" });
  };

  const validateToken = async (token: string) => {
    try {
      await axios.get(`${API_BASE_URL}/validate-token/`, {
        headers: { Authorization: `Token ${token}` },
      });
      return true;
    } catch (error) {
      console.error("Token validation failed:", error);
      logout();
      return false;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Token ${token}`;
      validateToken(token).then((isValid) => {
        setIsAuthenticated(isValid);
      });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, userInfo, login, logout, validateToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 